import { useEffect, useRef } from 'react';

interface FlowFieldOverlayProps {
  opacity?: number;
}

export default function FlowFieldOverlay({ opacity = 0.8 }: FlowFieldOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;

    const ctx = c.getContext('2d');
    if (!ctx) return;

    let w = (c.width = window.innerWidth);
    let h = (c.height = window.innerHeight);
    let t = 0;

    const onResize = () => {
      w = c.width = window.innerWidth;
      h = c.height = window.innerHeight;
    };
    window.addEventListener('resize', onResize);

    // Convert hex like #RRGGBB or #RGB to rgba(r,g,b,a)
    const hexToRgba = (hex: string, alpha: number) => {
      let h = hex.trim();
      if (h.startsWith('#')) h = h.slice(1);
      if (h.length === 3) {
        h = h.split('').map(ch => ch + ch).join('');
      }
      if (h.length !== 6) return `rgba(10,116,255,${alpha})`;
      const r = parseInt(h.slice(0,2), 16);
      const g = parseInt(h.slice(2,4), 16);
      const b = parseInt(h.slice(4,6), 16);
      return `rgba(${r},${g},${b},${alpha})`;
    };

    const draw = () => {
      t += 0.0035;
      ctx.clearRect(0, 0, w, h);
      ctx.lineWidth = 0.8;
      // Read header accent each frame so effect updates when theme/accent changes
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-header-accent')
        .trim() || '#0A74FF';
      const c0 = hexToRgba(accent, 0.5);
      const c1 = hexToRgba(accent, 0.35);
      for (let x = 0; x < w; x += 24) {
        for (let y = 0; y < h; y += 24) {
          const angle = (Math.sin(x * 0.002 + t) + Math.cos(y * 0.002 - t)) * Math.PI;
          const x2 = x + Math.cos(angle) * 18;
          const y2 = y + Math.sin(angle) * 18;
          const g = ctx.createLinearGradient(x, y, x2, y2);
          g.addColorStop(0, c0);
          g.addColorStop(1, c1);
          ctx.strokeStyle = g;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x2, y2);
          ctx.stroke();
        }
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
